/**
 * ProjectCalendarTab — month calendar driven by the same data as the Schedule.
 *
 * Reads and writes through useProjectSchedule, so the two views cannot disagree:
 * previously each derived dates from `project.start_date + start_day` in its own
 * copy of the formula, and one copy drifted.
 *
 * Because dates are now absolute, a task with no dates appears on NO day. All 52
 * existing tasks are in that state, so an "Unscheduled" tray is not a nicety —
 * without it the calendar would look empty and the tasks unreachable.
 */
import { useState, useMemo, useCallback } from 'react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2, CalendarDays } from 'lucide-react';
import { Button } from '../ui/button';
import useProjectSchedule from '../../hooks/useProjectSchedule';
import {
  mapTasksToCalendar, unscheduledTasks, countTasksInMonth, dateToIso,
} from '../../lib/scheduleAdapter';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_COLORS = {
  pending: { bar: '#d1d5db', edge: '#9ca3af', text: '#374151' },
  in_progress: { bar: '#60a5fa', edge: '#3b82f6', text: '#0b2f6b' },
  completed: { bar: '#34d399', edge: '#10b981', text: '#04442f' },
  blocked: { bar: '#fca5a5', edge: '#ef4444', text: '#6b1010' },
};

function TaskPill({ task, onClick }) {
  const c = STATUS_COLORS[task.status] || STATUS_COLORS.pending;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onClick(task); }}
      title={`${task.task_name || 'Untitled'} · ${task.status}`}
      className="truncate text-[10px] leading-tight px-1 py-0.5 rounded-sm cursor-pointer"
      style={{ background: c.bar, color: c.text, fontWeight: 600, borderLeft: `2px solid ${c.edge}` }}
    >
      {task.task_name || 'Untitled'}
    </div>
  );
}

export default function ProjectCalendarTab({ project }) {
  const { tasks, loading, saving, addTask, updateTask } = useProjectSchedule(project?.id);
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [expandedDay, setExpandedDay] = useState(null);

  const dayMap = useMemo(() => mapTasksToCalendar(tasks), [tasks]);
  const unscheduled = useMemo(() => unscheduledTasks(tasks), [tasks]);
  const monthCount = useMemo(
    () => countTasksInMonth(dayMap, month.getFullYear(), month.getMonth()),
    [dayMap, month]
  );

  const days = useMemo(() => {
    const from = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const to = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const out = [];
    for (let d = from; d <= to; d = addDays(d, 1)) out.push(new Date(d));
    return out;
  }, [month]);

  // Clicking an empty day creates a one-day task there — the quickest way to
  // get something onto the calendar.
  const handleDayClick = useCallback((day) => {
    const iso = dateToIso(day);
    addTask({ task_name: 'New task', status: 'pending', start_date: iso, end_date: iso });
  }, [addTask]);

  // Scheduling from the tray: give an unscheduled task this day.
  const scheduleOnDay = useCallback((task, day) => {
    const iso = dateToIso(day);
    updateTask(task.id, { start_date: iso, end_date: iso });
  }, [updateTask]);

  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading calendar…
      </div>
    );
  }

  const today = new Date();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {format(month, 'MMMM yyyy')}
          </h3>
          <span className="text-xs" style={{ color: saving ? '#c9a84c' : '#9ca3af' }}>
            {saving ? 'Saving…' : `${monthCount} ${monthCount === 1 ? 'task' : 'tasks'} this month`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setMonth(m => subMonths(m, 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMonth(startOfMonth(new Date()))}>Today</Button>
          <Button variant="outline" size="sm" onClick={() => setMonth(m => addMonths(m, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="rounded-md overflow-hidden" style={{ border: '1px solid #e5e4df', background: '#fff' }}>
        <div className="grid grid-cols-7" style={{ background: '#f8f7f4', borderBottom: '1px solid #e5e4df' }}>
          {WEEKDAYS.map((w, i) => (
            <div key={w} className="py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider"
                 style={{ color: i >= 5 ? '#d1d5db' : '#6b7280' }}>
              {w}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map(day => {
            const key = dateToIso(day);
            const dayTasks = dayMap[key] || [];
            const inMonth = isSameMonth(day, month);
            const isToday = isSameDay(day, today);
            const weekend = day.getDay() === 0 || day.getDay() === 6;
            const showAll = expandedDay === key;
            const visible = showAll ? dayTasks : dayTasks.slice(0, 3);

            return (
              <div
                key={key}
                onClick={() => handleDayClick(day)}
                className="relative border-b border-r flex flex-col cursor-pointer"
                style={{
                  borderColor: '#f0efec', minHeight: 84,
                  background: inMonth ? (weekend ? 'rgba(0,0,0,0.025)' : '#fff') : '#fafaf7',
                }}
              >
                <div className="px-1 pt-1">
                  <span className="text-[10px] font-medium inline-flex items-center justify-center"
                        style={{
                          color: !inMonth ? '#d1d5db' : isToday ? '#fff' : weekend ? '#9ca3af' : '#6b7280',
                          background: isToday ? '#c9a84c' : 'transparent',
                          borderRadius: isToday ? '50%' : 0,
                          width: isToday ? 18 : 'auto', height: isToday ? 18 : 'auto',
                        }}>
                    {format(day, 'd')}
                  </span>
                </div>

                <div className="flex-1 px-0.5 mt-0.5 space-y-0.5 overflow-hidden">
                  {visible.map(t => (
                    <TaskPill key={`${t.id}-${key}`} task={t} onClick={() => {}} />
                  ))}
                  {dayTasks.length > 3 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedDay(showAll ? null : key); }}
                      className="text-[10px] w-full text-center"
                      style={{ color: '#c9a84c' }}
                    >
                      {showAll ? 'show less' : `+${dayTasks.length - 3} more`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Without this tray, tasks with no dates would be invisible here. */}
      {unscheduled.length > 0 && (
        <div className="rounded-md p-3" style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}>
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-4 h-4" style={{ color: '#c9a84c' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#c9a84c' }}>
              Unscheduled ({unscheduled.length})
            </span>
            <span className="text-xs" style={{ color: '#9ca3af' }}>
              — click a task, then a day, to schedule it
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {unscheduled.map(t => (
              <button
                key={t.id}
                onClick={() => scheduleOnDay(t, month)}
                title={`Schedule on 1 ${format(month, 'MMMM yyyy')}`}
                className="text-xs px-2 py-1 rounded border transition-colors hover:border-[#c9a84c]"
                style={{ borderColor: 'rgba(201,168,76,0.3)', color: '#1a1a2e', background: '#fff' }}
              >
                {t.task_name || 'Untitled'}
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs" style={{ color: '#9ca3af' }}>
        Click an empty day to add a task there. Set exact dates in the Schedule tab.
      </p>
    </div>
  );
}
